import { useCallback, useEffect, useRef, useState } from "react";

// Per-order browser GPS capture (Website Takeaway/Dine-in checkout only).
//  • one bounded getCurrentPosition per attempt (maximumAge: 0 — never a cached/previous fix), no watchPosition
//  • high-accuracy first (8 s); on timeout / poor accuracy one low-accuracy retry (6 s)
//  • an outdated capture is refreshed in the BACKGROUND while the sheet is open (bounded, timer at expiry — no polling)
//  • Place Order never waits: snapshot() is synchronous and reports the best state available right now
//  • late callbacks from an older attempt or an older checkout are ignored (attempt + checkout ids)
export const FRESH_MS = 5 * 60 * 1000;          // a capture older than this is no longer described as "current"
export const LOW_ACCURACY_M = 300;
export const MAX_BG_REFRESH = 2;                 // automatic background refreshes per checkout session
const HIGH_TIMEOUT_MS = 8000, LOW_TIMEOUT_MS = 6000;

export const readPermissionState = async () => {
  try {
    if (!navigator.permissions?.query) return "unsupported";
    const p = await navigator.permissions.query({ name: "geolocation" });
    return p?.state || "unknown";
  } catch { return "unknown"; }
};

const getPosition = (opts) => new Promise((resolve) => {
  let done = false;
  const finish = (v) => { if (!done) { done = true; clearTimeout(timer); resolve(v); } };
  const timer = setTimeout(() => finish({ error: { code: 3 } }), (opts.timeout || HIGH_TIMEOUT_MS) + 500);
  try { navigator.geolocation.getCurrentPosition((pos) => finish({ pos }), (error) => finish({ error }), opts); }
  catch (error) { finish({ error }); }
});

const newCheckoutId = () => (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random().toString(16).slice(2)}`).replace(/-/g, "");
const isFreshAt = (at) => !!at && Date.now() - new Date(at).getTime() <= FRESH_MS;

export const useOrderLocation = (active) => {
  // `loc` = last RESULT for this checkout (idle | captured | denied | timeout | unavailable); `busy` = an attempt is in flight.
  const [loc, setLoc] = useState({ status: "idle", attempts: 0 });
  const [busy, setBusy] = useState(false);
  const locRef = useRef(loc); locRef.current = loc;
  const attemptRef = useRef(0);
  const inflightRef = useRef(false);
  const bgRef = useRef(0);
  // Checkout session id: created when the sheet opens, cleared when it closes — a late GPS callback from a
  // previous checkout can never attach to a newer one.
  const checkoutRef = useRef(null);

  const capture = useCallback(async () => {
    if (inflightRef.current) return;             // a rerender/navigation must not start a second capture
    inflightRef.current = true;
    const attempt = ++attemptRef.current;
    const myCheckout = checkoutRef.current;
    if (!myCheckout) { inflightRef.current = false; return; }
    const alive = () => attempt === attemptRef.current && myCheckout === checkoutRef.current;
    setBusy(true);
    const permission_state = await readPermissionState();
    if (!navigator.geolocation) {
      inflightRef.current = false;
      if (alive()) { setBusy(false); setLoc((c) => ({ status: "unavailable", reason: "geolocation_unsupported", permission_state, attempts: c.attempts + 1, checkoutId: myCheckout, source: "gps" })); }
      return;
    }
    if (alive()) setLoc((c) => ({ ...c, permission_state, checkoutId: myCheckout }));
    let r = await getPosition({ enableHighAccuracy: true, timeout: HIGH_TIMEOUT_MS, maximumAge: 0 });
    let tries = 1;
    const poor = (x) => x.pos && (x.pos.coords.accuracy == null || x.pos.coords.accuracy > LOW_ACCURACY_M);
    if (r.error?.code === 3 || poor(r)) {        // bounded low-accuracy retry
      const r2 = await getPosition({ enableHighAccuracy: false, timeout: LOW_TIMEOUT_MS, maximumAge: 0 });
      tries = 2;
      if (r2.pos && (!r.pos || (r2.pos.coords.accuracy ?? Infinity) < (r.pos.coords.accuracy ?? Infinity))) r = r2;
      else if (!r.pos && !r2.pos) r = r2.error?.code === 1 ? r2 : r;
    }
    inflightRef.current = false;
    if (!alive()) return;                        // superseded attempt or a different checkout — drop silently
    setBusy(false);
    setLoc((c) => {
      const attempts = c.attempts + tries;
      if (r.pos) {
        const acc = r.pos.coords.accuracy;
        return { status: "captured", lat: r.pos.coords.latitude, lng: r.pos.coords.longitude, accuracy_m: acc,
                 captured_at: new Date(r.pos.timestamp || Date.now()).toISOString(), low_accuracy: acc == null || acc > LOW_ACCURACY_M,
                 permission_state: permission_state === "prompt" ? "granted" : permission_state, attempts, checkoutId: myCheckout, source: "gps", refresh_failed: false };
      }
      const code = r.error?.code;
      // A failed REFRESH keeps the earlier coordinates (still shown with their original capture time, never relabelled current).
      if (c.status === "captured" && code !== 1) return { ...c, attempts, refresh_failed: true };
      const status = code === 1 ? "denied" : code === 3 ? "timeout" : "unavailable";
      return { status, reason: code === 1 ? "permission_denied" : code === 3 ? "position_timeout" : "position_unavailable",
               permission_state: code === 1 ? "denied" : permission_state, attempts, checkoutId: myCheckout, source: "gps" };
    });
  }, []);

  // One automatic attempt per checkout session (not per rerender); user can Retry/Refresh explicitly.
  useEffect(() => {
    if (!active) { checkoutRef.current = null; return; }
    if (checkoutRef.current) return;
    checkoutRef.current = newCheckoutId();
    attemptRef.current += 1; inflightRef.current = false; bgRef.current = 0;
    setLoc({ status: "idle", attempts: 0 }); setBusy(false);
    capture();
  }, [active, capture]);

  // Background freshness: one timer at the moment the capture becomes outdated → bounded refresh (no watchPosition/polling).
  useEffect(() => {
    if (!active || loc.status !== "captured" || !loc.captured_at || bgRef.current >= MAX_BG_REFRESH) return undefined;
    const myCheckout = checkoutRef.current;
    const delay = Math.max(0, new Date(loc.captured_at).getTime() + FRESH_MS - Date.now());
    const t = setTimeout(() => {
      if (myCheckout !== checkoutRef.current || inflightRef.current || bgRef.current >= MAX_BG_REFRESH) return;
      bgRef.current += 1;
      capture();
    }, delay);
    return () => clearTimeout(t);
  }, [active, loc.status, loc.captured_at, loc.attempts, capture]);

  const isFresh = (at = loc.captured_at) => isFreshAt(at);

  // Synchronous snapshot for THIS checkout only — never waits for GPS/permissions. Old coordinates go out as
  // freshness "stale" with their ORIGINAL capture time; an in-flight first attempt is reported as "pending".
  const snapshot = () => {
    const id = checkoutRef.current;
    const cur = locRef.current;
    const mine = !!id && cur.checkoutId === id;
    const base = { source: "gps", permission_state: (mine && cur.permission_state) || "unknown", attempts: (mine && cur.attempts) || 0 };
    if (mine && cur.status === "captured") {
      return { ...base, status: "captured", lat: cur.lat, lng: cur.lng, accuracy_m: cur.accuracy_m, captured_at: cur.captured_at,
               low_accuracy: !!cur.low_accuracy, freshness: isFreshAt(cur.captured_at) ? "fresh" : "stale" };
    }
    if (inflightRef.current && id) return { ...base, status: "pending", reason: "capture_in_progress" };
    if (!mine || cur.status === "idle") return { ...base, status: "unavailable", reason: "not_captured" };
    return { ...base, status: cur.status, ...(cur.reason ? { reason: cur.reason } : {}) };
  };

  return { loc, busy, capture, isFresh, snapshot };
};
